/**
 * GroupPill — canonical user group color config and reusable pill component.
 *
 * Single source of truth for all user group badge/pill styling throughout the app.
 *
 * Usage (component):
 *   <GroupPill group="admin" />
 *   <GroupPill group="clinicalmgr" className="ring-2 ring-offset-1 ring-blue-400" />
 *
 * Usage (utility):
 *   getGroupConfig('qa')  → { label: 'QA', className: 'bg-orange-100 text-orange-800 border-orange-200 font-semibold text-xs rounded border' }
 *
 * Group color scheme:
 *   admin       → purple
 *   owner       → slate-700 (dark)
 *   clinician   → blue
 *   clinicalmgr → indigo
 *   billing     → yellow
 *   qa          → orange
 *   coordinator → teal
 *   scheduler   → teal
 *   user        → gray
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface GroupConfig {
  /** Human-readable display label (e.g. "Clinical Mgr") */
  label: string;
  /** Full Tailwind class string for the pill element */
  className: string;
}

// ─── Utility ──────────────────────────────────────────────────────────────────

const BASE = 'font-semibold text-xs rounded border';

/**
 * Returns the display label and full pill className for a user group key.
 * Matching is case-insensitive. Falls back to gray styling with the raw value as label.
 *
 * @example
 *   const { label, className } = getGroupConfig('clinicalmgr');
 *   // label    → "Clinical Mgr"
 *   // className → "bg-indigo-100 text-indigo-800 border-indigo-200 font-semibold text-xs rounded border"
 */
export function getGroupConfig(group: string): GroupConfig {
  switch (group.toLowerCase()) {
    case 'admin':       return { label: 'Admin',        className: `bg-purple-100 text-purple-800 border-purple-200 ${BASE}` };
    case 'owner':       return { label: 'Owner',        className: `bg-slate-700 text-white border-slate-600 ${BASE}` };
    case 'clinician':   return { label: 'Clinician',    className: `bg-blue-100 text-blue-800 border-blue-200 ${BASE}` };
    case 'clinicalmgr': return { label: 'Clinical Mgr', className: `bg-indigo-100 text-indigo-800 border-indigo-200 ${BASE}` };
    case 'billing':     return { label: 'Billing',      className: `bg-yellow-100 text-yellow-800 border-yellow-200 ${BASE}` };
    case 'qa':          return { label: 'QA',           className: `bg-orange-100 text-orange-800 border-orange-200 ${BASE}` };
    case 'coordinator': return { label: 'Coordinator',  className: `bg-teal-100 text-teal-800 border-teal-200 ${BASE}` };
    case 'scheduler':   return { label: 'Scheduler',    className: `bg-teal-100 text-teal-800 border-teal-200 ${BASE}` };
    case 'readonly':    return { label: 'Read-only',    className: `bg-gray-100 text-gray-700 border-gray-300 ${BASE}` };
    case 'user':        return { label: 'User',         className: `bg-gray-100 text-gray-800 border-gray-200 ${BASE}` };
    default:            return { label: group,          className: `bg-gray-100 text-gray-800 border-gray-200 ${BASE}` };
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface GroupPillProps {
  /** User group key: admin | owner | clinician | clinicalmgr | billing | qa | coordinator | scheduler | user */
  group: string;
  /** Override display label (defaults to canonical label from getGroupConfig) */
  label?: string;
  /** Extra Tailwind classes appended to the root element (e.g. ring classes for selected state) */
  className?: string;
}

/**
 * GroupPill — canonical user group badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + group-specific
 * background, text color, and matching border.
 */
export function GroupPill({ group, label, className = '' }: GroupPillProps) {
  const config = getGroupConfig(group);
  const displayLabel = label ?? config.label;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 ${config.className} ${className}`.trim()}>
      {displayLabel}
    </span>
  );
}

export default GroupPill;
